import { Navigate, useLocation } from "react-router-dom";
import { useAuth, type AppRole } from "@/contexts/AuthContext";
import { AuthSpinner } from "@/components/AuthSpinner";
import { canRoleAccess, homeForRole } from "@/lib/roleRoutes";

/**
 * Redirects users to the right place based on auth + role.
 * Used for any route that should NOT be visited by a logged-in user (e.g. /auth, /signup-*).
 */
export function RedirectIfAuthed() {
  const { user, role, loading, refreshing, roleError } = useAuth();
  const location = useLocation();
  if (loading || refreshing) return <AuthSpinner label="Checking session…" />;
  if (!user) return null;
  if (roleError) {
    return (
      <Navigate
        to="/role-error"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }
  // The session can hydrate a tick before the role query finishes. Do not
  // redirect with role=null, because homeForRole(null) is /auth and creates
  // the exact stuck sign-in loop users were seeing.
  if (!role) return <AuthSpinner label="Checking session…" />;
  // If the user is mid-way through a flow that needs a session before it
  // can finish (e.g. Google OAuth signup or an email confirmation that still
  // has to claim a coach invite), let the page render so it can complete the
  // flow itself. The payload lives in localStorage (it must survive a new tab
  // after the confirmation email); the old sessionStorage key is still read
  // for anyone mid-flight from a previous version.
  if (
    typeof window !== "undefined" &&
    (window.localStorage.getItem("pending_invite_signup_v1") ||
      window.sessionStorage.getItem("pending_invite_signup_v1"))
  ) {
    return null;
  }
  // An already-signed-in athlete opening a coach invite link must not be
  // silently bounced home with the invite unclaimed — send them to the
  // linking screen with the code pre-filled.
  const inviteFromUrl =
    new URLSearchParams(location.search).get("invite") ??
    (location.pathname.startsWith("/onboard/")
      ? decodeURIComponent(location.pathname.slice("/onboard/".length))
      : null);
  if (inviteFromUrl && role !== "admin") {
    return <Navigate to={`/link-coach?invite=${encodeURIComponent(inviteFromUrl)}`} replace />;
  }
  const next = new URLSearchParams(location.search).get("next");
  const safeNext = next && next.startsWith("/") && !next.startsWith("//") && canRoleAccess(role, next)
    ? next
    : null;
  return <Navigate to={safeNext ?? homeForRole(role)} replace />;
}

/**
 * Gates a logged-in route. Optionally restricts to one or more roles.
 * - Unauthenticated → /auth (preserving the attempted path for post-login redirect).
 * - Wrong role → safe redirect to that role's home.
 */
export function RequireAuth({
  children,
  role: requiredRole,
}: {
  children: JSX.Element;
  role?: AppRole | AppRole[];
}) {
  const { user, role, loading, refreshing, roleError } = useAuth();
  const location = useLocation();

  if (loading || refreshing) return <AuthSpinner label="Loading…" />;

  if (!user) {
    return (
      <Navigate
        to="/auth"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  // Role lookup failed (network, RLS, etc.) → safe error page.
  if (roleError) {
    return (
      <Navigate
        to="/role-error"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  if (requiredRole) {
    // Race-condition guard: AuthContext fetches the role asynchronously
    // (deferred from onAuthStateChange via setTimeout). Right after signup
    // or login, `user` flips to a value before the role lookup completes.
    // During that window `loading` and `refreshing` can both be false while
    // `role` is still null — without this guard we'd bounce a freshly-
    // signed-in user to /403 even though they have the correct role.
    if (!role && !roleError) {
      return <AuthSpinner label="Loading…" />;
    }
    const allowed = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    // Admin always passes through (super-user).
    const ok = role === "admin" || allowed.includes(role);
    if (!ok) {
      return (
        <Navigate
          to="/403"
          replace
          state={{ from: `${location.pathname}${location.search}` }}
        />
      );
    }
  }

  return children;
}
